const statusMap: Record<IdentAppointmentStatus, IdentificationStatus> = {
  initialized: 'initialized',
  unknown: 'initialized',
  inprogress: 'inprogress',
  success: 'activatingQES',
  failure: 'failure',
}

export const useIdentStore = defineStore('ident', {
  state: () => ({
    eidasSelfIdentStatus: null as IdentificationStatus | null,
    eidasSelfIdentURL: '',
    zertesAutoIdentStatus: null as IdentificationStatus | null,
    zertesAutoIdentURL: '',
    identInProgress: false,
    identMethod: null as IdentMethod | null,
  }),
  getters: {
    eidasOngoingSelfIdent(): boolean {
      return this.eidasSelfIdentStatus !== null && useUserStore().highestSignatureQuality !== 'qes'
    },
    eidasOngoingIdent(): boolean {
      return this.eidasOngoingSelfIdent
    },
    zertesOngoingAutoIdent(): boolean {
      return this.zertesAutoIdentStatus !== null && useUserStore().highestSignatureQuality !== 'qes'
    },
    zertesOngoingIdent(): boolean {
      return this.zertesOngoingAutoIdent
    },
  },
  actions: {
    setIdentUrlStatus(info: IdentInfo, type: 'eidasSelfIdent' | 'zertesAutoIdent') {
      const status = statusMap[info.appointmentStatus] || null
      this[`${type}Status`] = status
      this[`${type}URL`] = info.identUrl
    },
    async doIdentIfNotInProgress(identFn: () => Promise<void>) {
      if (this.identInProgress) return
      this.identInProgress = true
      await identFn()
      this.identInProgress = false
    },
    async openStatusPage() {
      const { isDirectSign, directSignSession } = useDirectSignStore()
      if (isDirectSign) {
        await navigateTo({
          name: 'direct-sign-identification-id-userid',
          params: {
            id: directSignSession!.auth.password,
            userid: directSignSession!.auth.username,
          },
          query: {
            primaryAction: 'refresh',
          },
        })
      } else {
        await navigateTo({ name: 'profile-signature-standards' })
      }
    },
    async eidasGetAllIdentInfo() {
      await this.eidasGetSelfIdentInfo()
    },
    async eidasGenerateSelfIdent(srid?: string) {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      await this.doIdentIfNotInProgress(async () => {
        const { identUrl } = useDirectSignStore().isDirectSign
          ? await directIdentRepo.createEidasSelfIdent(srid)
          : await identRepo.createEidasSelfIdent(srid)
        this.eidasSelfIdentURL = identUrl
      })
    },
    async eidasGetSelfIdentInfo() {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      const info = useDirectSignStore().isDirectSign
        ? await directIdentRepo.getEidasSelfIdentInfo()
        : await identRepo.getEidasSelfIdentInfo()

      this.setIdentUrlStatus(info, 'eidasSelfIdent')
      return { appointmentStatus: info.appointmentStatus, identUrl: info.identUrl }
    },
    async eidasDeleteSelfIdent(email: string) {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      useDirectSignStore().isDirectSign
        ? await directIdentRepo.deleteEidasSelfIdent(email)
        : await identRepo.deleteEidasSelfIdent(email)
    },
    async checkIsIdentificationCovered(data: { srId?: string; legislation?: Legislation }) {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      const { password, isDirectSign } = useDirectSignStore()
      const { isCovered } = isDirectSign
        ? await directIdentRepo.getCoverageInfo(data.srId ?? password)
        : await identRepo.getCoverageInfo(data.srId, data.legislation)

      return isCovered
    },
    async zertesGetAllIdentInfo() {
      await this.zertesGetAutoIdentInfo()
    },
    async zertesGenerateAutoIdent(srid?: string) {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      await this.doIdentIfNotInProgress(async () => {
        const { identUrl } = useDirectSignStore().isDirectSign
          ? await directIdentRepo.createZertesSelfIdent(srid)
          : await identRepo.createZertesSelfIdent(srid)
        this.zertesAutoIdentURL = identUrl
      })
    },
    async zertesGetAutoIdentInfo() {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      const info = useDirectSignStore().isDirectSign
        ? await directIdentRepo.getZertesSelfIdentInfo()
        : await identRepo.getZertesSelfIdentInfo()

      this.setIdentUrlStatus(info, 'zertesAutoIdent')
      return { appointmentStatus: info.appointmentStatus, identUrl: info.identUrl }
    },
    async zertesDeleteAutoIdent(email: string) {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      useDirectSignStore().isDirectSign
        ? await directIdentRepo.deleteZertesSelfIdent(email)
        : await identRepo.deleteZertesSelfIdent(email)
    },
    async getIdentMethod(srId: string, sid: string): Promise<IdentMethod> {
      const { identificationRepository: identRepo, directIdentificationRepository: directIdentRepo } = useApi()

      const identMethod = useDirectSignStore().isDirectSign
        ? await directIdentRepo.getIdentMethod(srId, sid)
        : await identRepo.getIdentMethod(srId, sid)
      this.identMethod = identMethod
      return identMethod
    },
  },
})
